import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { Box, FormControl, SelectChangeEvent, TextField, Grid, MenuItem } from '@mui/material';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import LeavePageModal from '../../../../components/shared/dialogs/LeavePageModal';
import { UserContext } from '../../../../components/shared/useUser';
import { CurrencyConversionContent, ExchangeRateRequest } from '../../../../app/services/api/orderManagementClient';

const currencyOptions = [
  { code: 'EUR' },
  { code: 'USD' },
  { code: 'JPY' },
  { code: 'AUD' },
  { code: 'CAD' },
  { code: 'CHF' },
  { code: 'CNY' },
  { code: 'NZD' },
  { code: 'SEK' },
  { code: 'NOK' },
  { code: 'MXN' },
  { code: 'SGD' },
  { code: 'HKD' },
  { code: 'INR' },
  { code: 'ZAR' },
];

interface SubmitModalProps {
  openModal: boolean;
  onConfirm: (request: ExchangeRateRequest) => void;
  selectedRate: CurrencyConversionContent | undefined;
  onCancel: () => void;
}

const CreateUpdateRateModal: React.FC<SubmitModalProps> = ({ openModal, onConfirm, selectedRate, onCancel }) => {
  const { t } = useTranslation('manageExchangeRates');
  const { selectedStore } = useContext(UserContext);

  const [request, setRequest] = useState<ExchangeRateRequest>({
    id: 0,
    storeNumber: selectedStore?.storeNumber ?? '',
    currency: '',
    rate: undefined,
    fromDate: '',
  });

  const [initialRequest, setInitialRequest] = useState<ExchangeRateRequest | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const textFieldMargin = { mb: 8 };

  useEffect(() => {
    if (openModal) {
      getData();
    }
  }, [selectedRate, openModal]);

  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getData = () => {
    setIsLoading(true);
    const newRequest: ExchangeRateRequest = {
      id: selectedRate?.ID ?? 0,
      storeNumber: selectedStore?.storeNumber ?? '',
      fromDate: selectedRate?.ConversionDate
        ? parseDotNetDateToInput(selectedRate.ConversionDate)
        : parseDotNetDateToInput(new Date().toISOString()),
      rate: selectedRate?.ConversionRate ?? undefined,
      currency: selectedRate?.ToCurrency ?? '',
    };

    setRequest(newRequest);
    setInitialRequest(newRequest);
    setIsLoading(false);
  };

  const isFormDirty = () => {
    if (!request || !initialRequest) return false;
    return JSON.stringify(request) !== JSON.stringify(initialRequest);
  };

  const handleChange = (field: keyof ExchangeRateRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>,
  ) => {
    const value: string | number = event.target.value as string;

    setRequest((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    if (isFormDirty()) {
      setShowLeaveModal(true);
    } else {
      onCancel();
      setRequest({
        id: 0,
        storeNumber: selectedStore?.storeNumber ?? '',
        currency: '',
        rate: undefined,
        fromDate: '',
      });
    }
  };

  const handleConfirm = () => {
    onConfirm(request);
    setRequest({
      id: 0,
      storeNumber: selectedStore?.storeNumber ?? '',
      currency: '',
      rate: undefined,
      fromDate: '',
    });
    onCancel();
  };

  const parseDotNetDateToInput = (dateString: string) => {
    if (!dateString) return '';
    const match = /\/Date\((\d+)([+-]\d+)?\)\//.exec(dateString);
    let dateObj;
    if (match) {
      const timestamp = parseInt(match[1], 10);
      dateObj = new Date(timestamp);
    } else {
      dateObj = new Date(dateString);
    }
    if (isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const minDate = getTodayString();

  const isValid = request.currency.length > 0 && request.rate && request.rate > 0 && request.fromDate.length > 0;

  if (isLoading) return <LoadingWheel />;

  return (
    <>
      <Dialog
        open={openModal}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        aria-labelledby="customized-dialog-title"
        aria-describedby="customized-dialog-description"
        sx={{
          '& .MuiDialog-container': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="displayMD">{t('manageRate')}</Typography>
            <Typography variant="textMD"
              color="text.secondary">
              {t('editExistingRate')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <IconButton
              aria-label="close"
              onClick={handleCancel}
              sx={{ color: (theme) => theme.palette.custom.gray[700] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 7, pb: 4 }}>
          <Grid container
            spacing={4}
            sx={{ maxWidth: '100%' }}
            pt={5}>
            <Grid item
              xs={12}
              md={5}>
              <Box display="flex"
                alignItems="baseline"
                gap={2}>
                <Typography sx={{ minWidth: 70 }}>{t('GBP to')}</Typography>
                <TextField
                  select
                  fullWidth
                  required
                  label={t('currency')}
                  value={request.currency}
                  onChange={handleChange('currency')}
                  sx={textFieldMargin}
                  inputProps={{ min: 0 }}
                >
                  {currencyOptions.map((option) => (
                    <MenuItem key={option.code} 
                      value={option.code}>
                      {option.code}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <FormControl fullWidth>
                <TextField
                  required
                  fullWidth
                  sx={textFieldMargin}
                  label={t('exchangeRate')}
                  value={request.rate}
                  onChange={handleChange('rate')}
                  type="number"
                  inputProps={{ step: '0.01' }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  required
                  fullWidth
                  label={t('fromDate')}
                  sx={textFieldMargin}
                  value={request.fromDate}
                  onChange={handleChange('fromDate')}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: minDate,
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pr: 7, pb: 7, pt: 2, justifyContent: 'flex-end' }}>
          <Button variant="secondary"
            size="lg"
            onClick={handleCancel}
            sx={{ borderRadius: '8px', px: 3 }}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            disabled={!isValid}
            onClick={handleConfirm}
            sx={{ borderRadius: '8px', px: 3 }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog >
      <LeavePageModal
        open={showLeaveModal}
        onConfirmUrl=""
        onClose={() => setShowLeaveModal(false)}
        onConfirm={() => {
          handleConfirm();
          setShowLeaveModal(false);
        }}
        onCancel={() => {
          setShowLeaveModal(false);
          onCancel();
          setRequest({
            id: 0,
            storeNumber: selectedStore?.storeNumber ?? '',
            currency: '',
            rate: 0.0,
            fromDate: '',
          });
        }}
      />
    </>
  );
};

export default CreateUpdateRateModal;
